// src/components/roads/CreateRoadModal.js
// --- UPDATE: v2.0 — AI Road Helper + field explanations
//   • Live AI Road Helper above the tabs: readiness score, which AI features the
//     new road will support, what is missing and why; "Add" switches to the
//     right tab and focuses the field
//   • (?) explainer on every field (what / why / example / which AI uses it)
//   • Tab badges show how many important fields are still empty per tab
//   • Coordinate sanity check (outside The Bahamas / swapped / positive longitude)
//   • FIX: elevation 0 m was saved as null (truthiness check)
//   • FIX: useColorModeValue was called inside JSX — hoisted to the top
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, FormControl, FormLabel, Input, Select, Textarea, HStack, VStack,
    SimpleGrid, Icon, Text, Box, useColorModeValue, Tabs, TabList, TabPanels, Tab, TabPanel,
    InputGroup, InputRightAddon, Tooltip,
    // --- UPDATE
    Badge,
    // --- END UPDATE
} from '@chakra-ui/react';
import { MdAddRoad, MdInfoOutline, MdStraighten, MdLocationOn } from 'react-icons/md';
// --- UPDATE
import AiRoadHelper, { FieldHint, analyzeRoad, FIELD_GUIDE } from './AiRoadHelper';

const toNumberOrNull = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
// --- END UPDATE

const CreateRoadModal = ({ isOpen, onClose, onSubmit }) => {
    // 🎨 Theme Colors
    const modalBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const tabActiveBg = useColorModeValue('blue.50', 'blue.900');
    const tabActiveColor = useColorModeValue('blue.600', 'blue.300');
    // --- UPDATE: hoisted from JSX
    const headerBg = useColorModeValue('gray.50', 'gray.800');
    const tabListBg = useColorModeValue('white', 'gray.800');
    const infoBg = useColorModeValue('blue.50', 'blue.900');
    const infoText = useColorModeValue('blue.600', 'blue.200');
    // --- END UPDATE

    // ─── Form State ─────────────────────────────────────────────────────────
    const initialState = {
        name: '',
        road_code: '',
        constituency: '',
        location_description: '',
        status: 'operational',
        classification: '',
        surface_type: '',
        length_m: '',
        width_m: '',
        lanes: '',
        speed_limit_kmh: '',
        latitude: '',
        longitude: '',
        elevation: '', // 🚀 Surveyed Elevation (m)
        notes: ''
    };

    const [formData, setFormData] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData(initialState);
            setActiveTab(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // --- UPDATE: AI helper wiring
    const analysis = useMemo(() => analyzeRoad(formData, 'create'), [formData]);
    const missingByTab = useMemo(() => {
        const counts = [0, 0, 0];
        analysis.checks.forEach(c => {
            if (c.status !== 'ok' && c.level !== 'optional' && c.tab != null) counts[c.tab] += 1;
        });
        return counts;
    }, [analysis]);

    const focusField = useCallback((fieldId) => {
        const guideKey = fieldId === 'latitude' || fieldId === 'longitude' ? 'coordinates' : fieldId;
        const tab = FIELD_GUIDE[guideKey]?.tab;
        if (tab != null) setActiveTab(tab);
        setTimeout(() => {
            const el = document.getElementById(`road-field-${fieldId}`);
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
        }, 120);
    }, []);
    // --- END UPDATE

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            // Package the data exactly how the backend normalizeRoadPayload expects it
            const payload = {
                name: formData.name,
                location_description: formData.location_description,
                status: formData.status,
                // --- UPDATE: 0 is a valid value — only blanks become null
                latitude: toNumberOrNull(formData.latitude),
                longitude: toNumberOrNull(formData.longitude),
                elevation: toNumberOrNull(formData.elevation), // 🚀 Surveyed Elevation (m)
                // --- END UPDATE
                metadata: {
                    road_code: formData.road_code,
                    constituency: formData.constituency,
                    length_m: formData.length_m ? Number(formData.length_m) : null,
                    width_m: formData.width_m ? Number(formData.width_m) : null,
                    lanes: formData.lanes ? Number(formData.lanes) : null,
                    speed_limit_kmh: formData.speed_limit_kmh ? Number(formData.speed_limit_kmh) : null,
                    surface_type: formData.surface_type || null,
                    classification: formData.classification || null,
                    notes: formData.notes
                }
            };

            await onSubmit(payload);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(6px)" />
            <ModalContent borderRadius="2xl" bg={modalBg} borderWidth="1px" borderColor={borderColor} boxShadow="2xl">
                <ModalHeader borderBottomWidth="1px" borderColor={borderColor} bg={headerBg} pb={4}>
                    <HStack spacing={3}>
                        <Box bg="blue.500" p={2.5} borderRadius="xl" boxShadow="sm">
                            <Icon as={MdAddRoad} color="white" boxSize={6} />
                        </Box>
                        <Box>
                            <Text fontSize="lg" fontWeight="900">Add New Road Asset</Text>
                            <Text fontSize="xs" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="wider">
                                FlowSense Network Inventory
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton mt={2} />

                <ModalBody p={0}>
                    {/* --- UPDATE: live AI Road Helper */}
                    <Box px={5} pt={4}>
                        <AiRoadHelper road={formData} context="create" variant="compact" onFix={focusField} defaultOpen={false} />
                    </Box>
                    {/* --- END UPDATE */}
                    <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue" isFitted>
                        <TabList borderBottomWidth="1px" borderColor={borderColor} bg={tabListBg} px={2} pt={2}>
                            <Tab fontSize="sm" fontWeight="600" _selected={{ color: tabActiveColor, bg: tabActiveBg, borderTopRadius: 'lg', borderBottom: 'none' }}>
                                <Icon as={MdInfoOutline} mr={2} /> Basic Info
                                {/* --- UPDATE */}
                                {missingByTab[0] > 0 && <Badge ml={2} colorScheme="purple" borderRadius="full" fontSize="9px">{missingByTab[0]}</Badge>}
                                {/* --- END UPDATE */}
                            </Tab>
                            <Tab fontSize="sm" fontWeight="600" _selected={{ color: tabActiveColor, bg: tabActiveBg, borderTopRadius: 'lg', borderBottom: 'none' }}>
                                <Icon as={MdStraighten} mr={2} /> Physical Specs
                                {/* --- UPDATE */}
                                {missingByTab[1] > 0 && <Badge ml={2} colorScheme="purple" borderRadius="full" fontSize="9px">{missingByTab[1]}</Badge>}
                                {/* --- END UPDATE */}
                            </Tab>
                            <Tab fontSize="sm" fontWeight="600" _selected={{ color: tabActiveColor, bg: tabActiveBg, borderTopRadius: 'lg', borderBottom: 'none' }}>
                                <Icon as={MdLocationOn} mr={2} /> Geo & ML Data
                                {/* --- UPDATE */}
                                {missingByTab[2] > 0 && <Badge ml={2} colorScheme="purple" borderRadius="full" fontSize="9px">{missingByTab[2]}</Badge>}
                                {/* --- END UPDATE */}
                            </Tab>
                        </TabList>

                        <TabPanels p={5}>
                            {/* TAB 1: BASIC INFO */}
                            <TabPanel p={0}>
                                <VStack spacing={5} align="stretch">
                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl isRequired id="road-field-name">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Road Name<FieldHint field="name" /></FormLabel>
                                            <Input name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Carmichael Road" borderRadius="lg" />
                                        </FormControl>
                                        <FormControl id="road-field-road_code">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Road Code / ID<FieldHint field="road_code" /></FormLabel>
                                            <Input name="road_code" value={formData.road_code} onChange={handleChange} placeholder="e.g. CR-001" borderRadius="lg" fontFamily="mono" />
                                        </FormControl>
                                    </SimpleGrid>

                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl id="road-field-constituency">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Constituency<FieldHint field="constituency" /></FormLabel>
                                            <Select name="constituency" value={formData.constituency} onChange={handleChange} borderRadius="lg" placeholder="Select Constituency">
                                                <option value="Carmichael">Carmichael</option>
                                                <option value="Golden Gates">Golden Gates</option>
                                                <option value="Killarney">Killarney</option>
                                                <option value="Bain & Grants Town">Bain & Grants Town</option>
                                                <option value="Englerston">Englerston</option>
                                                <option value="Fox Hill">Fox Hill</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl id="road-field-status">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Operational Status<FieldHint field="status" /></FormLabel>
                                            <Select name="status" value={formData.status} onChange={handleChange} borderRadius="lg">
                                                <option value="operational">Operational</option>
                                                <option value="maintenance_required">Maintenance Required</option>
                                                <option value="under_maintenance">Under Maintenance</option>
                                                <option value="closed">Closed</option>
                                                <option value="planned">Planned</option>
                                            </Select>
                                        </FormControl>
                                    </SimpleGrid>

                                    <FormControl id="road-field-location_description">
                                        <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Location Description<FieldHint field="location_description" /></FormLabel>
                                        <Textarea name="location_description" value={formData.location_description} onChange={handleChange} placeholder="General area or landmarks..." rows={2} borderRadius="lg" fontSize="sm" />
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                            {/* TAB 2: PHYSICAL SPECS */}
                            <TabPanel p={0}>
                                <VStack spacing={5} align="stretch">
                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl id="road-field-classification">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Classification<FieldHint field="classification" /></FormLabel>
                                            <Select name="classification" value={formData.classification} onChange={handleChange} borderRadius="lg" placeholder="Select type">
                                                <option value="highway">Highway</option>
                                                <option value="arterial">Arterial</option>
                                                <option value="collector">Collector</option>
                                                <option value="local">Local</option>
                                                <option value="service">Service</option>
                                                <option value="residential">Residential</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl id="road-field-surface_type">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Surface Material<FieldHint field="surface_type" /></FormLabel>
                                            <Select name="surface_type" value={formData.surface_type} onChange={handleChange} borderRadius="lg" placeholder="Select material">
                                                <option value="asphalt">Asphalt</option>
                                                <option value="concrete">Concrete</option>
                                                <option value="gravel">Gravel</option>
                                                <option value="dirt">Dirt</option>
                                                <option value="paved_block">Paved Block</option>
                                                <option value="chip_seal">Chip Seal</option>
                                            </Select>
                                        </FormControl>
                                    </SimpleGrid>

                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl id="road-field-length_m">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Total Length<FieldHint field="length_m" /></FormLabel>
                                            <InputGroup size="md">
                                                <Input type="number" name="length_m" value={formData.length_m} onChange={handleChange} placeholder="0" borderRadius="lg" />
                                                <InputRightAddon children="meters" borderRightRadius="lg" />
                                            </InputGroup>
                                        </FormControl>
                                        <FormControl id="road-field-width_m">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Width<FieldHint field="width_m" /></FormLabel>
                                            <InputGroup size="md">
                                                <Input type="number" name="width_m" value={formData.width_m} onChange={handleChange} placeholder="0" borderRadius="lg" />
                                                <InputRightAddon children="meters" borderRightRadius="lg" />
                                            </InputGroup>
                                        </FormControl>
                                    </SimpleGrid>

                                    <SimpleGrid columns={2} spacing={4}>
                                        <FormControl id="road-field-lanes">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Number of Lanes<FieldHint field="lanes" /></FormLabel>
                                            <Input type="number" name="lanes" value={formData.lanes} onChange={handleChange} placeholder="e.g. 2" borderRadius="lg" />
                                        </FormControl>
                                        <FormControl id="road-field-speed_limit_kmh">
                                            <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Speed Limit<FieldHint field="speed_limit_kmh" /></FormLabel>
                                            <InputGroup size="md">
                                                <Input type="number" name="speed_limit_kmh" value={formData.speed_limit_kmh} onChange={handleChange} placeholder="0" borderRadius="lg" />
                                                <InputRightAddon children="km/h" borderRightRadius="lg" />
                                            </InputGroup>
                                        </FormControl>
                                    </SimpleGrid>
                                </VStack>
                            </TabPanel>

                            {/* TAB 3: GEO & ML */}
                            <TabPanel p={0}>
                                <VStack spacing={5} align="stretch">
                                    <Box p={3} bg={infoBg} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                                        <Text fontSize="sm" color={infoText} fontWeight="600">
                                            <Icon as={MdInfoOutline} mr={2} />
                                            Coordinate and elevation data assist Machine Learning models in cross-referencing weather patterns, runoff velocities, and flood overtopping risks.
                                        </Text>
                                    </Box>

                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                        <FormControl id="road-field-latitude">
                                            <Tooltip label="Decimal degrees (e.g., 25.0443)" placement="top" hasArrow>
                                                <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" cursor="help">Center Latitude<FieldHint field="coordinates" /></FormLabel>
                                            </Tooltip>
                                            <Input type="number" step="any" name="latitude" value={formData.latitude} onChange={handleChange} placeholder="25.0443" borderRadius="lg" fontFamily="mono" />
                                        </FormControl>
                                        <FormControl id="road-field-longitude">
                                            <Tooltip label="Decimal degrees (e.g., -77.3963)" placement="top" hasArrow>
                                                <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" cursor="help">Center Longitude<FieldHint field="coordinates" /></FormLabel>
                                            </Tooltip>
                                            <Input type="number" step="any" name="longitude" value={formData.longitude} onChange={handleChange} placeholder="-77.3963" borderRadius="lg" fontFamily="mono" />
                                        </FormControl>
                                        <FormControl id="road-field-elevation">
                                            <Tooltip label="Surveyed center-line crown elevation in meters relative to MSL/datum" placement="top" hasArrow>
                                                <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" cursor="help">Surveyed Elevation (m)<FieldHint field="elevation" /></FormLabel>
                                            </Tooltip>
                                            <InputGroup size="md">
                                                <Input type="number" step="0.01" name="elevation" value={formData.elevation} onChange={handleChange} placeholder="3.45" borderRadius="lg" fontFamily="mono" />
                                                <InputRightAddon children="m" borderRightRadius="lg" />
                                            </InputGroup>
                                        </FormControl>
                                    </SimpleGrid>

                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">Additional Engineering Notes</FormLabel>
                                        <Textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Historical context, specific drainage issues, etc." rows={3} borderRadius="lg" fontSize="sm" />
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                        </TabPanels>
                    </Tabs>
                </ModalBody>

                <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={headerBg} borderBottomRadius="2xl">
                    {/* --- UPDATE: readiness at a glance */}
                    <Text fontSize="xs" color={mutedColor} mr="auto">
                        AI readiness <b>{analysis.score}%</b> · {analysis.grade.label}
                    </Text>
                    {/* --- END UPDATE */}
                    <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
                        Cancel
                    </Button>
                    <Button 
                        colorScheme="blue" 
                        onClick={handleSubmit} 
                        isLoading={submitting} 
                        isDisabled={!formData.name}
                        leftIcon={<Icon as={MdAddRoad} />}
                        borderRadius="lg"
                        boxShadow="md"
                    >
                        Save Asset to Network
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreateRoadModal;